/**
 * Mobile v2 (ratified CD pass §3) — the native welcome / login
 * (`/welcome`, Capacitor shell only). A Daylight brand moment (mark →
 * wordmark → serif tagline) over a faint pace-line motif, then TWO auth
 * buttons in the thumb zone and a legal line with a visible Privacy
 * Policy link. Replaces the in-shell web landing as the signed-out entry.
 *
 * The boot splash (native-root) settles into this surface — the
 * mark/wordmark occupy the SAME coordinates on the same cream so launch
 * → welcome is one continuous beat (D4; geometry mirrored in
 * native-root.module.css — keep the two in sync).
 *
 * Sign-in reuses the EXISTING OAuth machinery unchanged (flow logic is
 * fixed, surface only): `signInWithGoogle()` (system-browser-branches
 * inside the shell via openNativeOAuth) + the `oauthError` chip state
 * machine from AuthContext.
 *
 * apple-login-v1 — the Apple button is NO LONGER a design-only slot. It
 * calls `signInWithApple()` (native ASAuthorization sheet →
 * signInWithIdToken; see lib/appleAuth.ts) and renders ONLY when the
 * `apple_login_v1` server flag is on. Note what that means for the OFF
 * path: this surface loses the decorative Apple button it ships today.
 * That is deliberate — a button that explains why it doesn't work is
 * worse than no button, and App Review sees the flag-ON build.
 * ⚠️ `useServerFlag` reads /api/config ONCE at boot and caches it, so
 * flipping the flag does not reach a running shell; verify on a cold
 * start.
 *
 * Returning-but-signed-out variant (§3.3): when a previous session was
 * observed on this device (the `tayro.hasSignedIn` flag) the copy
 * reframes around picking up where they left off. Same buttons.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppleMark } from '../components/brand/AppleMark';
import { TayroMark } from '../components/brand/TayroMark';
import { useAuthContext } from '../contexts/AuthContext';
import { useServerFlag } from '../hooks/useServerFlag';
import { oauthAuxClause } from '../lib/oauthChipCopy';
import styles from './native-welcome.module.css';

/** localStorage marker — "a session has been observed on this device".
 *  Deliberately written at the welcome/native-root layer, not inside
 *  AuthContext (the flag is a copy decision, not auth state). */
export const HAS_SIGNED_IN_KEY = 'tayro.hasSignedIn';

export function hasPreviousSession(): boolean {
  try {
    return localStorage.getItem(HAS_SIGNED_IN_KEY) === '1';
  } catch {
    return false; // localStorage unavailable — treat as first run.
  }
}

export function markSessionSeen(): void {
  try {
    localStorage.setItem(HAS_SIGNED_IN_KEY, '1');
  } catch {
    /* no-op — the flag only warms future copy */
  }
}

/* The auxiliary recovery clauses moved to `lib/oauthChipCopy.ts`
 * (apple-login-v1): they now depend on (primary × provider), and this
 * surface renders the same chip as the web hero — the two must not drift. */

/** Full-colour Google "G" — legal ONLY on a white/light fill (Google
 *  branding guidelines). The marine buttons elsewhere use the
 *  monochrome-white GoogleGMark; this white button gets the 4-colour. */
function GoogleGColor({ size = 18 }: { size?: number }): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" className={styles.gGlyph}>
      <path fill="#4285F4" d="M21.6 12.227c0-.668-.06-1.31-.171-1.926H12v3.643h5.382a4.6 4.6 0 0 1-1.996 3.018v2.51h3.232c1.89-1.74 2.982-4.302 2.982-7.245z" />
      <path fill="#34A853" d="M12 22c2.7 0 4.964-.895 6.618-2.428l-3.232-2.51c-.895.6-2.04.957-3.386.957-2.605 0-4.81-1.76-5.597-4.124H3.07v2.59A9.997 9.997 0 0 0 12 22z" />
      <path fill="#FBBC05" d="M6.403 13.895a6.005 6.005 0 0 1 0-3.79V7.515H3.07a9.997 9.997 0 0 0 0 8.97l3.333-2.59z" />
      <path fill="#EA4335" d="M12 5.98c1.47 0 2.79.504 3.83 1.498l2.868-2.868C16.96 3.005 14.695 2 12 2 8.114 2 4.757 4.237 3.07 7.515l3.333 2.59C7.19 7.74 9.394 5.98 12 5.98z" />
    </svg>
  );
}

export default function NativeWelcome(): JSX.Element {
  const navigate = useNavigate();
  const {
    user,
    signInWithGoogle,
    signInWithApple,
    oauthError,
    oauthErrorProvider,
    clearOAuthError,
  } = useAuthContext();
  // apple-login-v1 — WHICH provider is in flight. A shared boolean made this
  // surface say "Redirecting to Google…" while the ATHLETE was looking at
  // Apple's sheet, and swapped the untapped button's label too. `null` = idle.
  const [pendingProvider, setPendingProvider] = useState<'google' | 'apple' | null>(
    null,
  );
  const anyPending = pendingProvider !== null;
  // apple-login-v1 — the Apple button exists only when the server flag says so.
  const appleOn = useServerFlag('apple_login_v1');
  // 2.0 app pass, Part 7: the restyle of this surface, behind `onboarding_2_0_v1`. Off, nothing
  // below differs from what shipped (no attribute, the old kicker string).
  const onboarding2On = useServerFlag('onboarding_2_0_v1');
  // Captured once at mount so the variant can't flip mid-view when the
  // flag gets written on a successful sign-in.
  const [returning] = useState(hasPreviousSession);

  // Native OAuth returns via the tayro:// deep link → onAuthStateChange
  // populates `user` while this surface is still mounted. Record the
  // session and hand off to the app.
  useEffect(() => {
    if (!user) return;
    markSessionSeen();
    navigate('/today', { replace: true });
  }, [user, navigate]);

  const handleGoogle = async () => {
    clearOAuthError();
    setPendingProvider('google');
    try {
      await signInWithGoogle();
    } finally {
      setPendingProvider(null);
    }
  };

  /** The native sheet is modal and returns in-process, so unlike the Google
   *  system-browser hop there IS a moment to reset. A cancelled sheet resolves
   *  with no error set (AuthContext swallows it), so this lands back on an
   *  untouched welcome surface — no toast, no chip. */
  const handleApple = async () => {
    clearOAuthError();
    setPendingProvider('apple');
    try {
      await signInWithApple();
    } finally {
      setPendingProvider(null);
    }
  };

  return (
    <div className={styles.welcome} data-onb2={onboarding2On ? '' : undefined}>
      {/* Daylight pace-line motif — faint switchback strokes + one small
          --today dot (decorative). */}
      <svg
        className={styles.motif}
        viewBox="0 0 390 800"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden="true"
      >
        <path
          d="M-40 250 L120 120 L200 190 L360 40"
          fill="none"
          stroke="#EAEAE5"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M-20 720 L150 600 L240 670 L430 520"
          fill="none"
          stroke="#EAEAE5"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="360" cy="40" r="4" fill="var(--today)" />
      </svg>

      {/* D4 — brand block carried down from the splash: same mark size,
          same coordinates, same cream (geometry mirrored in
          native-root.module.css — keep the two in sync). */}
      <div className={styles.brand}>
        <TayroMark size={80} />
        <div className={styles.wordmark}>Tayro</div>
      </div>

      {returning && (
        <span className={styles.kicker}>{onboarding2On ? 'Welcome back' : 'WELCOME BACK'}</span>
      )}

      <p className={styles.tagline}>
        {returning ? (
          <>
            Your plan, your runs, your coaches, <span className={styles.taglineEm}>right where you left them.</span>
          </>
        ) : (
          <>
            A coaching team that knows your week, your race, your yesterday:{' '}
            <span className={styles.taglineEm}>part of the plan.</span>
          </>
        )}
      </p>

      <div className={styles.ctaZone}>
        {oauthError !== null ? (
          <div className={styles.errChip} role="alert" aria-live="assertive" data-state="error">
            <span className={styles.errDot} aria-hidden />
            <span className={styles.errPrimary}>{oauthError}</span>
            <span className={styles.errAux}>
              {oauthAuxClause(oauthError, oauthErrorProvider)}
            </span>
          </div>
        ) : pendingProvider === 'google' ? (
          // Google leaves for the system browser — a redirect, and it says so.
          <span className={styles.helper}>Redirecting to Google…</span>
        ) : pendingProvider === 'apple' ? (
          // Apple is a MODAL SHEET over this surface, not a redirect, so the
          // copy must not promise a hop that never happens.
          <span className={styles.helper}>Opening Apple sign-in…</span>
        ) : null}

        <button
          type="button"
          className={`${styles.authBtn} ${styles.google}`}
          onClick={() => void handleGoogle()}
          disabled={anyPending}
          aria-busy={pendingProvider === 'google'}
          data-state={pendingProvider === 'google' ? 'pending' : 'idle'}
        >
          {pendingProvider === 'google' ? (
            <span className={styles.spinner} aria-hidden="true" />
          ) : (
            <GoogleGColor size={18} />
          )}
          <span>
            {pendingProvider === 'google' ? 'Signing in…' : 'Continue with Google'}
          </span>
        </button>

        {appleOn && (
          <button
            type="button"
            className={`${styles.authBtn} ${styles.apple}`}
            onClick={() => void handleApple()}
            disabled={anyPending}
            aria-busy={pendingProvider === 'apple'}
            data-state={pendingProvider === 'apple' ? 'pending' : 'idle'}
          >
            {pendingProvider === 'apple' ? (
              <span className={styles.spinner} aria-hidden="true" />
            ) : (
              <AppleMark size={17} className={styles.appleGlyph} />
            )}
            <span>{pendingProvider === 'apple' ? 'Signing in…' : 'Sign in with Apple'}</span>
          </button>
        )}

        <p className={styles.legal}>
          {/* Founder ruling 2026-07-31 (option c, sequenced): no Terms link and
              no "agree to our Terms" assertion until a real terms document
              exists — /terms currently serves the SPA shell with a 200 (a soft
              404), so the old line asserted agreement to a contract that has
              never been written, in a build headed to App Review. The sentence
              returns when the document is real; a test pins the absence. */}
          By continuing you acknowledge our{' '}
          <a
            className={styles.legalLink}
            href="https://tayro.run/privacy"
            target="_blank"
            rel="noreferrer"
          >
            Privacy Policy
          </a>
          .
        </p>
      </div>
    </div>
  );
}
